import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Google from 'expo-auth-session/providers/google';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import * as WebBrowser from 'expo-web-browser';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Trans, useTranslation } from 'react-i18next';
import Svg, { Path } from 'react-native-svg';

import { getApiConfigDebugInfo } from '@/api/config';
import { GoogleIcon } from '@/components/brand/google-icon';
import { Loading } from '@/components/ui/loading';
import {
  oauthRedirectUri,
  publicConfig,
} from '@/config/public';
import { authStrings as strings } from '@/i18n/features/auth.i18n';
import {
  isAppleWebCancel,
  signInWithAppleWeb,
} from '@/lib/apple-sign-in-web';
import { otpRetrySeconds, otpSendLimitFrom } from '@/lib/otp-limit';
import { formatRetryCountdown } from '@/lib/retry-countdown';
import { createLatch } from '@/lib/single-tap';
import { notify } from '@/lib/dialog';
import {
  clearOAuthFragment,
  clearOAuthResidue,
} from '@/lib/oauth-residue';
import { useAuthStore } from '@/state/auth-store';
import {
  type AppColors,
  useAppTheme,
} from '@/theme/app-theme';

// NOTE: `maybeCompleteAuthSession()` now runs in `app/_layout.tsx`, at the root.
// It must execute on whatever URL the popup lands on — which is the bare origin,
// not this screen — and before the router can move it. See the comment there.

const EMAIL_RE =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function cleanEnv(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function cleanGoogleClientId(
  name: string,
  value: string | undefined,
) {
  const trimmed = cleanEnv(value);
  if (!trimmed) return undefined;

  if (
    !/^[\w.-]+\.apps\.googleusercontent\.com$/i.test(
      trimmed,
    )
  ) {
    console.warn(
      `[auth] Ignoring invalid ${name}. Expected a Google OAuth client ID ending in .apps.googleusercontent.com.`,
    );

    return undefined;
  }

  return trimmed;
}

// Google OAuth *client IDs* are PUBLIC identifiers sourced from public config
// (environments/<env>/public.yaml). An EXPO_PUBLIC_* env var may still override
// per developer. Values that aren't real client IDs (e.g. the dev "dummy-*"
// placeholders) are rejected by cleanGoogleClientId and disable Google sign-in.
const GOOGLE_CLIENT_IDS = {
  iosClientId: cleanGoogleClientId(
    'EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID',
    process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID ??
      publicConfig.oauth.googleIosClientId,
  ),

  androidClientId: cleanGoogleClientId(
    'EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID',
    process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID ??
      publicConfig.oauth.googleAndroidClientId,
  ),

  webClientId: cleanGoogleClientId(
    'EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID',
    process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ??
      publicConfig.oauth.googleWebClientId,
  ),
};

const GOOGLE_PLATFORM_CLIENT_ID =
  Platform.select({
    android:
      GOOGLE_CLIENT_IDS.androidClientId,
    ios: GOOGLE_CLIENT_IDS.iosClientId,
    default: GOOGLE_CLIENT_IDS.webClientId,
  });

const GOOGLE_PLATFORM_CONFIGURED =
  Boolean(GOOGLE_PLATFORM_CLIENT_ID);

const GOOGLE_PLACEHOLDER_CLIENT_ID =
  'missing-google-client-id.apps.googleusercontent.com';

const GOOGLE_AUTH_REQUEST_CLIENT_IDS = {
  ...GOOGLE_CLIENT_IDS,
  iosClientId:
    Platform.OS === 'ios'
      ? GOOGLE_CLIENT_IDS.iosClientId ??
        GOOGLE_PLACEHOLDER_CLIENT_ID
      : GOOGLE_CLIENT_IDS.iosClientId,
  androidClientId:
    Platform.OS === 'android'
      ? GOOGLE_CLIENT_IDS.androidClientId ??
        GOOGLE_PLACEHOLDER_CLIENT_ID
      : GOOGLE_CLIENT_IDS.androidClientId,
  webClientId:
    Platform.OS === 'web'
      ? GOOGLE_CLIENT_IDS.webClientId ??
        GOOGLE_PLACEHOLDER_CLIENT_ID
      : GOOGLE_CLIENT_IDS.webClientId,
};

/**
 * How long the "signing you in" overlay may stay up before the screen is given
 * back. Only reached when the post-sign-in redirect never happens.
 */
const SIGN_IN_HANDOFF_TIMEOUT_MS = 20000;

// Where Google returns the sign-in popup on web.
//
// This must be a dedicated path. Left to itself `makeRedirectUri` produces the
// BARE ORIGIN (`Linking.createURL('')` → `new URL('', window.location.origin)`
// with the trailing slash stripped), and every page of this app carries
// `Cross-Origin-Opener-Policy`. Per the HTML spec's opener-policy matching, a
// response whose COOP is anything but `unsafe-none` coming from a document
// whose COOP is `unsafe-none` — accounts.google.com, which sends the header
// report-only — swaps browsing context group and nulls `window.opener`. The
// popup then posts the token to ITSELF and sits there, silently.
//
// `public/_headers` strips COOP from `/oauth/*` and from nowhere else, so the
// redirect has to land inside that prefix. See the note on `/*` there for the
// spec steps and how it was measured.
//
// `typeof window` is guarded because `app.json` sets `web.output: "static"`:
// this module is evaluated in Node during the export, where there is no
// `window`. The browser re-evaluates it on import, which is when the value is
// actually read.
const GOOGLE_WEB_REDIRECT_URI =
  Platform.OS === 'web' &&
  typeof window !== 'undefined'
    ? `${window.location.origin}/oauth/google/callback`
    : undefined;

const GOOGLE_AUTH_REQUEST_CONFIG = {
  ...GOOGLE_AUTH_REQUEST_CLIENT_IDS,
  redirectUri:
    Platform.OS === 'android'
      ? oauthRedirectUri
      : GOOGLE_WEB_REDIRECT_URI,
  // Always show Google's account chooser.
  //
  // Signing out clears OUR session (`clearLocalSession`) but it cannot touch
  // Google's own SSO session at accounts.google.com — nothing a relying party
  // does can. With no `prompt` parameter and one already-signed-in Google
  // account that has previously granted consent, Google skips the chooser and
  // redirects straight back with a fresh id_token. The visible effect is that
  // "sign out" followed by "Continue with Google" silently signs the SAME
  // person back in with no interaction, which on a shared browser hands one
  // person's account to the next.
  //
  // The provider translates this to `prompt=select_account` (see
  // `expo-auth-session/src/providers/Google.ts` — `selectAccount` is the only
  // input that sets it). It forces the chooser every time; it does not, and
  // cannot, log anyone out of Google.
  selectAccount: true,
};

const FONT_FAMILY = Platform.select({
  ios: 'Avenir Next',
  android: 'sans-serif',
  default: undefined,
});

const DISPLAY_FONT = Platform.select({
  ios: 'Georgia',
  android: 'serif',
  default: 'Georgia',
});

type WelcomeStyles =
  ReturnType<typeof createStyles>;

type AuthButtonProps = {
  label: string;
  onPress: () => void;
  leftIcon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  compact?: boolean;
  veryCompact?: boolean;
  colors: AppColors;
  isDark: boolean;
  styles: WelcomeStyles;
};

function AuthButton({
  label,
  onPress,
  leftIcon,
  loading = false,
  disabled = false,
  variant = 'secondary',
  compact = false,
  veryCompact = false,
  colors,
  isDark,
  styles,
}: AuthButtonProps) {
  const isDisabled = disabled || loading;
  const isPrimary = variant === 'primary';

  const gradientColors = isDark
    ? ([
        colors.primaryStrong,
        colors.primary,
        '#4D8EF2',
      ] as const)
    : ([
        '#1760D9',
        '#2F73DF',
        '#1C5FD0',
      ] as const);

  // Icon + label as a plain, Android-safe row. No gap/zIndex/elevation/relative
  // positioning — those left the whole content row unpainted on Android. Android
  // primary buttons render this row directly in the Pressable; adding a fill
  // layer between them made the blue shape paint while the label disappeared in
  // Expo Go.
  const content = (
    <View style={styles.authButtonContent} pointerEvents="none">
      {loading ? (
        <ActivityIndicator
          color={
            isPrimary
              ? colors.white
              : isDark
                ? colors.textStrong
                : '#142D50'
          }
        />
      ) : leftIcon ? (
        <View style={styles.authButtonIcon}>{leftIcon}</View>
      ) : null}

      <Text
        numberOfLines={1}
        style={[
          styles.authButtonLabel,
          compact && styles.authButtonLabelCompact,
          veryCompact && styles.authButtonLabelVeryCompact,
          isPrimary
            ? styles.primaryButtonLabel
            : styles.secondaryButtonLabel,
        ]}>
        {label}
      </Text>
    </View>
  );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={isDisabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.authButton,
        compact &&
          styles.authButtonCompact,
        veryCompact &&
          styles.authButtonVeryCompact,
        isPrimary &&
          styles.authButtonPrimaryShell,
        !isPrimary &&
          styles.authButtonSecondaryShell,
        pressed &&
          !isDisabled &&
          styles.buttonPressed,
        isDisabled &&
          styles.buttonDisabled,
      ]}>
      {isPrimary && Platform.OS === 'android' ? (
        content
      ) : isPrimary ? (
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.authButtonFill}>
          {content}
        </LinearGradient>
      ) : (
        <View style={[styles.authButtonFill, styles.secondaryButton]}>
          {content}
        </View>
      )}
    </Pressable>
  );
}

type UserBubbleProps = {
  size: number;
  style: StyleProp<ViewStyle>;
  strong?: boolean;
  colors: AppColors;
  styles: WelcomeStyles;
};

function UserBubble({
  size,
  style,
  strong = false,
  colors,
  styles,
}: UserBubbleProps) {
  return (
    <View
      style={[
        styles.userBubble,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
        },
        strong &&
          styles.userBubbleStrong,
        style,
      ]}>
      <Ionicons
        name="person"
        size={size * 0.46}
        color={
          strong
            ? colors.white
            : colors.primary
        }
      />
    </View>
  );
}

type NearbyGraphicProps = {
  compact?: boolean;
  veryCompact?: boolean;
  colors: AppColors;
  styles: WelcomeStyles;
};

function NearbyGraphic({
  compact = false,
  veryCompact = false,
  colors,
  styles,
}: NearbyGraphicProps) {
  const { t } = useTranslation();
  const scale = veryCompact
    ? 0.66
    : compact
      ? 0.78
      : 1;

  const frameSize = 310 * scale;
  const offset =
    (frameSize - 310) / 2;

  return (
    <View
      style={[
        styles.radarFrame,
        {
          width: frameSize,
          height: frameSize,
          marginTop: veryCompact
            ? 10
            : compact
              ? 16
              : 34,
        },
      ]}>
      <View
        style={[
          styles.radar,
          {
            top: offset,
            left: offset,
            transform: [
              {
                scale,
              },
            ],
          },
        ]}>
        <View
          style={[
            styles.radarRing,
            styles.radarRingOuter,
          ]}
        />

        <View
          style={[
            styles.radarRing,
            styles.radarRingMiddle,
          ]}
        />

        <View
          style={[
            styles.radarRing,
            styles.radarRingInner,
          ]}
        />

        <View style={styles.radarGlow} />

        <UserBubble
          size={46}
          style={styles.userTopLeft}
          colors={colors}
          styles={styles}
        />

        <UserBubble
          size={46}
          style={styles.userTopRight}
          colors={colors}
          styles={styles}
        />

        <UserBubble
          size={44}
          style={styles.userBottomLeft}
          colors={colors}
          styles={styles}
        />

        <UserBubble
          size={62}
          style={styles.userCenter}
          strong
          colors={colors}
          styles={styles}
        />

        <View
          style={styles.messageBubble}>
          <Svg
            width={18}
            height={16}
            viewBox="0 0 18 16"
            style={styles.messageTail}
            pointerEvents="none">
            <Path
              d="M16 0 C 13 6, 8 9, 2 15 C 4 8, 7 5, 10 1 Z"
              fill={colors.surface}
            />
          </Svg>

          <Text style={styles.messageText}>
            {t(strings.greeting)}
          </Text>
        </View>
      </View>
    </View>
  );
}

type HeroProps = {
  compact?: boolean;
  compactHeight?: boolean;
  veryCompactHeight?: boolean;
  colors: AppColors;
  styles: WelcomeStyles;
};

function Hero({
  compact = false,
  compactHeight = false,
  veryCompactHeight = false,
  colors,
  styles,
}: HeroProps) {
  const { t } = useTranslation();
  return (
    <View
      style={[
        styles.hero,
        compact && styles.heroCompact,
        compact &&
          compactHeight &&
          styles.heroCompactHeight,
      ]}>
      <Text
        accessibilityRole="header"
        style={[
          styles.logo,
          compactHeight &&
            styles.logoCompact,
          veryCompactHeight &&
            styles.logoVeryCompact,
        ]}>
        <Text style={styles.logoDark}>
          opn
        </Text>

        <Text style={styles.logoBlue}>
          cue.
        </Text>
      </Text>


      <Text
        style={[
          styles.subtitle,
          compactHeight &&
            styles.subtitleCompact,
          veryCompactHeight &&
            styles.subtitleVeryCompact,
        ]}>
        {t(strings.headline)}
      </Text>

      {!compact ? (
        <NearbyGraphic
          compact={compactHeight}
          veryCompact={
            veryCompactHeight
          }
          colors={colors}
          styles={styles}
        />
      ) : null}
    </View>
  );
}

type DividerProps = {
  compact?: boolean;
  veryCompact?: boolean;
  styles: WelcomeStyles;
};

function Divider({
  compact = false,
  veryCompact = false,
  styles,
}: DividerProps) {
  return (
    <View
      style={[
        styles.dividerRow,
        compact &&
          styles.dividerRowCompact,
        veryCompact &&
          styles.dividerRowVeryCompact,
      ]}>
      <View style={styles.dividerLine} />

      <View style={styles.dividerOrnament}>
        <View style={styles.dividerRing} />
        <View style={[styles.dividerDot, styles.dividerDotPrimary]} />
        <View style={[styles.dividerDot, styles.dividerDotStrong]} />
        <View style={styles.dividerRing} />
      </View>

      <View style={styles.dividerLine} />
    </View>
  );
}

export default function Welcome() {
  const router = useRouter();
  const { t } = useTranslation();

  const {
    colors,
    isDark,
    statusBarStyle,
  } = useAppTheme();

  const styles = useMemo(
    () => createStyles(colors, isDark),
    [colors, isDark],
  );

  const { height, width } =
    useWindowDimensions();

  const compactHeight = height < 1000;
  const veryCompactHeight = height < 740;
  const wideScreen = width >= 620;

  const horizontalPadding =
    wideScreen ? 40 : 24;

  const emailIconSize =
    veryCompactHeight
      ? 19
      : compactHeight
        ? 21
        : 23;

  const appleIconSize =
    veryCompactHeight
      ? 22
      : compactHeight
        ? 24
        : 26;

  const googleIconSize =
    veryCompactHeight
      ? 21
      : compactHeight
        ? 23
        : 25;

  const startEmail = useAuthStore(
    (state) => state.startEmail,
  );

  const signInWithGoogle =
    useAuthStore(
      (state) =>
        state.signInWithGoogle,
    );

  const signInWithApple =
    useAuthStore(
      (state) =>
        state.signInWithApple,
    );

  const [mode, setMode] = useState<
    'options' | 'email'
  >('options');

  const [email, setEmail] =
    useState('');

  const [error, setError] =
    useState<string>();

  const [loading, setLoading] =
    useState(false);

  // `loading` disables the button, but only from the next render. This refuses
  // the second press of a double tap — and the keyboard's submit key racing the
  // button, since both are wired to `onContinue` — in the same tick it arrives.
  // A duplicate `/auth/start` invalidates the code the first one emailed.
  const submitLatch = useRef(
    createLatch(),
  ).current;

  const [social, setSocial] =
    useState<
      'google' | 'apple' | null
    >(null);

  /**
   * A provider credential is in hand and the app is finishing sign-in.
   *
   * Separate from `social`, which is the per-button pending state and is
   * cleared the instant the backend call settles. That clear was the gap: the
   * screen went back to looking untouched while the redirect to onboarding was
   * still being issued — the buttons idle, no spinner anywhere, nothing saying
   * a sign-in had succeeded. On a slow connection that is several seconds of a
   * screen that appears to have ignored the tap, right after the user came back
   * from Google or Apple expecting to land somewhere.
   *
   * So this one is NOT cleared on success. It stays up until the route change
   * unmounts the screen, which is the only moment the wait is actually over.
   */
  const [completing, setCompleting] =
    useState(false);

  const [
    appleAvailable,
    setAppleAvailable,
  ] = useState(false);

  const handledGoogleIdTokenRef =
    useRef<string | null>(null);
  const googleTokenWaitTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

  // On web, the ID token can come back directly from promptAsync(). On native
  // Android/iOS, expo-auth-session starts a code flow and then auto-exchanges
  // the code; the ID token arrives through the hook response after that.
  const [request, googleResponse, promptAsync] =
    Google.useIdTokenAuthRequest(
      GOOGLE_AUTH_REQUEST_CONFIG,
      {
        native: oauthRedirectUri,
      },
    );

  function clearGoogleTokenWaitTimeout() {
    if (googleTokenWaitTimeoutRef.current) {
      clearTimeout(
        googleTokenWaitTimeoutRef.current,
      );
      googleTokenWaitTimeoutRef.current = null;
    }
  }

  /**
   * The overlay cannot be allowed to hang.
   *
   * It is deliberately not cleared on success, so the only thing that normally
   * takes it away is the screen unmounting. If the redirect never lands — the
   * routing guard re-issues `router.replace` for about three seconds and then
   * gives up with a warning — an uncleared overlay would be the same "wondering
   * what happened" this exists to fix, wearing a spinner.
   *
   * Long on purpose. This is a fallback for something broken, and a short one
   * would turn a slow-but-working sign-in on a bad connection into a false
   * failure. Matches the timeout the Google prompt already uses for the same
   * class of thing.
   */
  useEffect(() => {
    if (!completing) return undefined;

    const timer = setTimeout(() => {
      setCompleting(false);
      setError(t(strings.signInError));
    }, SIGN_IN_HANDOFF_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [completing, t]);

  useEffect(
    () => () => clearGoogleTokenWaitTimeout(),
    [],
  );

  useEffect(() => {
    if (Platform.OS === 'ios') {
      void AppleAuthentication
        .isAvailableAsync()
        .then(setAppleAvailable);
    }
  }, []);

  function showGoogleDevFailure(
    reason: string,
    details?: unknown,
  ) {
    setError(t(strings.signInError));

    if (!__DEV__) return;

    const api = getApiConfigDebugInfo();
    const detailText =
      details instanceof Error
        ? `${details.name}: ${details.message}`
        : typeof details === 'object' &&
            details !== null
          ? JSON.stringify(details, null, 2)
          : details
            ? String(details)
            : '';

    notify(
      'Google sign-in failed',
      [
        reason,
        `Platform: ${Platform.OS}`,
        `API: ${api.apiBaseUrl}`,
        `Mock: ${api.useMock}`,
        detailText,
      ]
        .filter(Boolean)
        .join('\n\n'),
    );
  }

  function startGoogleWaitTimeout(
    reason: string,
    details?: unknown,
    timeoutMs = 12000,
  ) {
    clearGoogleTokenWaitTimeout();
    googleTokenWaitTimeoutRef.current =
      setTimeout(() => {
        setSocial((current) => {
          if (current !== 'google') {
            return current;
          }

          console.warn(`[auth] ${reason}`);
          showGoogleDevFailure(reason, details);

          return null;
        });
      }, timeoutMs);
  }

  async function completeGoogleSignIn(
    idToken: string,
    source: string,
  ) {
    if (
      handledGoogleIdTokenRef.current ===
      idToken
    ) {
      return;
    }

    handledGoogleIdTokenRef.current = idToken;
    clearGoogleTokenWaitTimeout();

    // The token has been taken out of the round-trip; nothing should still be
    // holding a copy. `expo-web-browser` parks the popup's full URL — fragment
    // and ID token included — in localStorage, and only clears it if the flow
    // reaches an outcome in the opener. Both call sites reach this function, so
    // this is the one place that covers them.
    try {
      clearOAuthResidue();
      clearOAuthFragment();
    } catch (err) {
      // Storage can be unavailable (Safari private mode, blocked third-party
      // storage). Failing to tidy up must never fail the sign-in that just
      // succeeded.
      console.warn(
        '[auth] Could not clear OAuth residue.',
        err,
      );
    }

    try {
      setCompleting(true);
      await signInWithGoogle(idToken);
    } catch (err) {
      setCompleting(false);
      handledGoogleIdTokenRef.current = null;
      console.warn(
        `[auth] Google backend sign-in failed from ${source}.`,
        err,
      );
      showGoogleDevFailure(
        `Backend rejected the Google token (${source}).`,
        err,
      );
    } finally {
      setSocial(null);
    }
  }

  useEffect(() => {
    if (
      social !== 'google' ||
      !googleResponse
    ) {
      return;
    }

    if (
      googleResponse.type !== 'success'
    ) {
      setSocial(null);

      if (
        googleResponse.type === 'error'
      ) {
        clearGoogleTokenWaitTimeout();
        console.warn(
          '[auth] Google prompt returned an error.',
          googleResponse,
        );
        showGoogleDevFailure(
          'Google returned an auth error.',
          googleResponse,
        );
      }

      return;
    }

    const idToken =
      googleResponse.params?.id_token ??
      googleResponse.authentication?.idToken;

    if (!idToken) {
      clearGoogleTokenWaitTimeout();
      console.warn(
        '[auth] Google sign-in completed without an ID token.',
        googleResponse,
      );
      setSocial(null);
      showGoogleDevFailure(
        'Google completed, but Expo did not provide an ID token.',
        {
          type: googleResponse.type,
          params: googleResponse.params,
          hasAuthentication: Boolean(
            googleResponse.authentication,
          ),
        },
      );

      return;
    }

    void completeGoogleSignIn(
      idToken,
      'hook-response',
    );
  }, [googleResponse, social, t]);

  async function onContinue() {
    if (!submitLatch.claim()) {
      return;
    }

    try {
      await sendCode();
    } finally {
      submitLatch.release();
    }
  }

  async function sendCode() {
    if (!EMAIL_RE.test(email)) {
      setError(
        t(strings.invalidEmail),
      );

      return;
    }

    setError(undefined);
    setLoading(true);

    try {
      if (__DEV__) {
        const normalized = email.trim().toLowerCase();
        console.log('[auth:start] button pressed', {
          emailLength: normalized.length,
          emailDomain: normalized.includes('@')
            ? normalized.split('@').at(-1)
            : null,
          api: getApiConfigDebugInfo(),
        });
      }
      await startEmail(email);
      if (__DEV__) {
        console.log('[auth:start] completed; routing to verify');
      }
      // The address travels WITH the route. Expo Router restores params when
      // the app is relaunched after the OS has reclaimed it — which iOS and
      // Android both do while the user is in their mail app fetching the code —
      // and that is the one copy of it the store cannot keep.
      router.push({
        pathname: '/(auth)/verify',
        params: { email: email.trim().toLowerCase() },
      });
    } catch (err) {
      if (__DEV__) {
        console.warn('[auth:start] failed in welcome screen', {
          name: err instanceof Error ? err.name : 'UnknownError',
          message: err instanceof Error ? err.message : String(err),
        });
      }
      // The address's send budget names its own wait. Anything else keeps the
      // generic sentence — a countdown we do not have would be a guess.
      const refusal = otpSendLimitFrom(err);
      setError(
        refusal
          ? t(strings.tooManyAttemptsError, {
              countdown: formatRetryCountdown(
                otpRetrySeconds(refusal),
              ),
            })
          : t(strings.signInError),
      );
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    if (!GOOGLE_PLATFORM_CONFIGURED) {
      console.warn(
        `[auth] Google sign-in is not configured for ${Platform.OS}.`,
      );
      showGoogleDevFailure(
        `Google sign-in is not configured for ${Platform.OS}.`,
        GOOGLE_CLIENT_IDS,
      );

      return;
    }

    if (!request) {
      console.warn(
        '[auth] Google auth request is not ready yet.',
      );
      showGoogleDevFailure(
        'Google auth request is not ready yet.',
        {
          platform: Platform.OS,
          platformConfigured:
            GOOGLE_PLATFORM_CONFIGURED,
        },
      );

      return;
    }

    setSocial('google');
    setError(undefined);
    clearGoogleTokenWaitTimeout();

    try {
      startGoogleWaitTimeout(
        'Google prompt did not return to Opncue. The Android redirect callback may not be reaching the app.',
        {
          expectedAndroidRedirectUri:
            oauthRedirectUri,
        },
        20000,
      );

      const result =
        await promptAsync();

      clearGoogleTokenWaitTimeout();

      if (
        result.type !== 'success'
      ) {
        setSocial(null);

        if (
          result.type === 'error'
        ) {
          console.warn(
            '[auth] Google prompt returned an error.',
            result,
          );
          showGoogleDevFailure(
            'Google returned an auth error.',
            result,
          );
        }

        return;
      }

      const idToken =
        result.params?.id_token ??
        result.authentication?.idToken;

      if (!idToken) {
        // Native installed apps usually receive a code here first. The provider
        // auto-exchanges it and emits the ID token via `googleResponse`.
        startGoogleWaitTimeout(
          'Timed out waiting for Expo to exchange the Google code for an ID token.',
          {
            resultType: result.type,
            params: result.params,
            hasAuthentication: Boolean(
              result.authentication,
            ),
          },
        );
        return;
      }

      await completeGoogleSignIn(
        idToken,
        'prompt-result',
      );
    } catch (err) {
      clearGoogleTokenWaitTimeout();
      setSocial(null);

      console.warn(
        '[auth] Google prompt failed.',
        err,
      );
      showGoogleDevFailure(
        'Google prompt failed before sign-in completed.',
        err,
      );
    }
  }

  async function onApple() {
    if (Platform.OS === 'web') {
      try {
        setSocial('apple');

        const credential =
          await signInWithAppleWeb({
            clientId:
              publicConfig.oauth
                .appleWebClientId,
            redirectUri:
              publicConfig.oauth
                .appleWebRedirectUri,
          });

        setCompleting(true);
        await signInWithApple(
          credential.identityToken,
          credential.fullName,
          credential.nonce,
        );
      } catch (err) {
        setCompleting(false);

        /**
         * Cancellation is checked BEFORE anything is logged.
         *
         * A closed popup is a decision, not a failure. iOS suppresses its own
         * cancel (`ERR_REQUEST_CANCELED`); the web SDK rejects with a plain
         * object rather than an Error, so it used to fall through to the
         * failure notice and tell people who had just changed their mind to
         * try again. Logging first would fix the notice and keep the lie: the
         * default Sentry integrations turn `console` calls into breadcrumbs,
         * so every cancelled sign-in would attach a warning breadcrumb to
         * whatever unrelated error came next.
         */
        if (isAppleWebCancel(err)) return;

        /**
         * The technical reason goes to the console, never to the user.
         *
         * This used to render `err.message` directly, which was wrong twice
         * over. The `try` above spans Apple's own `auth.signIn()` and OUR
         * backend call, so the message could be an English sentence from
         * `apple-sign-in-web.ts`, a browser exception, or — worst — an
         * `ApiError` carrying server-authored text straight onto the screen.
         * The iOS branch below has always done it this way; this branch was
         * the outlier.
         */
        console.warn(
          '[auth] Apple web sign-in failed.',
          err,
        );

        notify(
          t(strings.appleFailedTitle),
          t(strings.appleFailedBody),
        );
      } finally {
        setSocial(null);
      }

      return;
    }

    if (Platform.OS !== 'ios') {
      notify(
        t(strings.appleOnlyOnIosTitle),
        t(strings.appleOnlyOnIosBody),
      );

      return;
    }

    if (!appleAvailable) {
      notify(
        t(strings.appleUnavailableTitle),
        t(strings.appleUnavailableBody),
      );

      return;
    }

    try {
      setSocial('apple');

      const credential =
        await AppleAuthentication
          .signInAsync({
            requestedScopes: [
              AppleAuthentication
                .AppleAuthenticationScope
                .FULL_NAME,

              AppleAuthentication
                .AppleAuthenticationScope
                .EMAIL,
            ],
          });

      if (
        !credential.identityToken
      ) {
        notify(
          t(strings.appleFailedTitle),
          t(strings.appleMissingTokenBody),
        );

        return;
      }

      const fullName = [
        credential.fullName
          ?.givenName,

        credential.fullName
          ?.familyName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();

      setCompleting(true);
      await signInWithApple(
        credential.identityToken,
        fullName || undefined,
      );
    } catch (err) {
      setCompleting(false);

      if (
        (err as {
          code?: string;
        }).code !==
        'ERR_REQUEST_CANCELED'
      ) {
        notify(
          t(strings.appleFailedTitle),
          t(strings.appleFailedBody),
        );
      }
    } finally {
      setSocial(null);
    }
  }

  return (
    <SafeAreaView
      style={styles.screen}
      edges={['top', 'bottom']}>
      <StatusBar
        style={statusBarStyle}
      />

      <KeyboardAvoidingView
        behavior="padding"
        style={styles.keyboardView}>
        <ScrollView
          bounces={false}
          keyboardDismissMode={
            Platform.OS === 'ios'
              ? 'interactive'
              : 'on-drag'
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={
            false
          }
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingHorizontal:
                horizontalPadding,
            },
            compactHeight &&
              styles.scrollContentCompact,
            veryCompactHeight &&
              styles.scrollContentVeryCompact,
          ]}>
          <View
            style={
              styles.contentContainer
            }>
            <Hero
              compact={mode === 'email'}
              compactHeight={
                compactHeight
              }
              veryCompactHeight={
                veryCompactHeight
              }
              colors={colors}
              styles={styles}
            />

            {mode === 'options' ? (
              <View
                style={[
                  styles.actions,
                  compactHeight &&
                    styles.actionsCompact,
                  veryCompactHeight &&
                    styles.actionsVeryCompact,
                ]}>
                <AuthButton
                  label={t(strings.continueWithEmail)}
                  variant="primary"
                  compact={compactHeight}
                  veryCompact={
                    veryCompactHeight
                  }
                  onPress={() =>
                    setMode('email')
                  }
                  colors={colors}
                  isDark={isDark}
                  styles={styles}
                  leftIcon={
                    <Ionicons
                      name="mail-outline"
                      size={emailIconSize}
                      color={colors.white}
                    />
                  }
                />

                <AuthButton
                  label={t(strings.continueWithApple)}
                  compact={compactHeight}
                  veryCompact={
                    veryCompactHeight
                  }
                  loading={
                    social === 'apple'
                  }
                  onPress={onApple}
                  colors={colors}
                  isDark={isDark}
                  styles={styles}
                  leftIcon={
                    <Ionicons
                      name="logo-apple"
                      size={appleIconSize}
                      color={
                        isDark
                          ? colors.textStrong
                          : '#000000'
                      }
                    />
                  }
                />

                <AuthButton
                  label={t(strings.continueWithGoogle)}
                  compact={compactHeight}
                  veryCompact={
                    veryCompactHeight
                  }
                  loading={
                    social === 'google'
                  }
                  disabled={
                    GOOGLE_PLATFORM_CONFIGURED &&
                    !request
                  }
                  onPress={onGoogle}
                  colors={colors}
                  isDark={isDark}
                  styles={styles}
                  leftIcon={
                    <GoogleIcon
                      size={googleIconSize}
                    />
                  }
                />

                <Divider
                  compact={compactHeight}
                  veryCompact={
                    veryCompactHeight
                  }
                  styles={styles}
                />

                <Text
                  style={[
                    styles.legalText,
                    compactHeight &&
                      styles.legalTextCompact,
                    veryCompactHeight &&
                      styles.legalTextVeryCompact,
                  ]}>
                  <Trans
                    i18nKey={strings.legalAgreement}
                    components={{
                      terms: (
                        <Text
                          accessibilityRole="link"
                          onPress={() =>
                            router.push('/legal/terms-of-service')
                          }
                          style={styles.linkText}
                        />
                      ),
                      privacy: (
                        <Text
                          accessibilityRole="link"
                          onPress={() =>
                            router.push('/legal/privacy-policy')
                          }
                          style={styles.linkText}
                        />
                      ),
                    }}
                  />
                </Text>
              </View>
            ) : (
              <View
                style={[
                  styles.emailPanel,
                  compactHeight &&
                    styles.emailPanelCompact,
                ]}>
                <View
                  style={
                    styles.emailHeading
                  }>
                  <Text
                    style={
                      styles.emailTitle
                    }>
                    {t(strings.emailTitle)}
                  </Text>

                  <Text
                    style={
                      styles.emailDescription
                    }>
                    {t(strings.emailDescription)}
                  </Text>
                </View>

                <View>
                  <Text
                    style={
                      styles.inputLabel
                    }>
                    {t(strings.emailLabel)}
                  </Text>

                  <View
                    style={[
                      styles.inputContainer,
                      error
                        ? styles.inputContainerError
                        : null,
                    ]}>
                    <Ionicons
                      name="mail-outline"
                      size={21}
                      color={
                        isDark
                          ? colors.icon
                          : '#75839A'
                      }
                    />

                    <TextInput
                      value={email}
                      onChangeText={(
                        value,
                      ) => {
                        setEmail(value);

                        if (error) {
                          setError(
                            undefined,
                          );
                        }
                      }}
                      placeholder={t(strings.emailPlaceholder)}
                      placeholderTextColor={
                        isDark
                          ? colors.textMuted
                          : '#A0AABC'
                      }
                      selectionColor={
                        colors.primary
                      }
                      keyboardAppearance={
                        isDark
                          ? 'dark'
                          : 'light'
                      }
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoCorrect={false}
                      autoComplete="email"
                      autoFocus
                      returnKeyType="go"
                      onSubmitEditing={
                        onContinue
                      }
                      style={styles.input}
                    />
                  </View>

                  {error ? (
                    <Text
                      style={
                        styles.errorText
                      }>
                      {error}
                    </Text>
                  ) : null}
                </View>

                <AuthButton
                  label={t(strings.sendCode)}
                  variant="primary"
                  compact={compactHeight}
                  veryCompact={
                    veryCompactHeight
                  }
                  loading={loading}
                  onPress={onContinue}
                  colors={colors}
                  isDark={isDark}
                  styles={styles}
                  leftIcon={
                    <Ionicons
                      name="arrow-forward"
                      size={21}
                      color={colors.white}
                    />
                  }
                />

                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    setError(undefined);
                    setMode('options');
                  }}
                  style={({ pressed }) => [
                    styles.backButton,
                    pressed &&
                      styles.buttonPressed,
                  ]}>
                  <Ionicons
                    name="arrow-back"
                    size={19}
                    color={
                      isDark
                        ? colors.primaryStrong
                        : '#245FC8'
                    }
                  />

                  <Text
                    style={
                      styles.backButtonText
                    }>
                    {t(strings.back)}
                  </Text>
                </Pressable>
              </View>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Over everything, and blocking: from here until the route changes there
          is nothing on this screen worth pressing, and a second tap on Continue
          would start a second sign-in. The app's one waiting state rather than a
          bare ActivityIndicator — it carries the label a screen reader
          announces, which a silent spinner does not. */}
      {completing ? (
        <View
          style={styles.completingOverlay}
          accessibilityViewIsModal
          // Nothing behind it is reachable, so nothing behind it should be
          // findable by assistive technology either.
          importantForAccessibility="yes"
          accessibilityLabel={t(
            strings.signingIn,
          )}>
          <Loading
            label={t(strings.signingIn)}
            color={colors.primary}
          />
        </View>
      ) : null}
    </SafeAreaView>
  );
}

function createStyles(
  colors: AppColors,
  isDark: boolean,
) {
  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor:
        colors.background,
    },

    keyboardView: {
      flex: 1,
    },

    // Opaque, not a scrim. A dimmed sign-in screen still reads as the sign-in
    // screen with something wrong with it; a plain waiting surface reads as the
    // app having moved on, which is what has actually happened by then.
    completingOverlay: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor:
        colors.background,
    },

    scrollContent: {
      flexGrow: 1,
      justifyContent: 'center',
      paddingTop: 20,
      paddingBottom: 12,
    },

    scrollContentCompact: {
      paddingTop: 8,
      paddingBottom: 6,
    },

    scrollContentVeryCompact: {
      paddingTop: 3,
      paddingBottom: 3,
    },

    contentContainer: {
      width: '100%',
      maxWidth: 540,
      alignSelf: 'center',
    },

    hero: {
      alignItems: 'center',
    },

    heroCompact: {
      paddingBottom: 30,
    },

    heroCompactHeight: {
      paddingBottom: 14,
    },

    logo: {
      fontFamily: FONT_FAMILY,
      fontSize: 58,
      lineHeight: 66,
      fontWeight: '500',
      letterSpacing: -4.2,
    },

    logoCompact: {
      fontSize: 48,
      lineHeight: 54,
      letterSpacing: -3.4,
    },

    logoVeryCompact: {
      fontSize: 42,
      lineHeight: 47,
      letterSpacing: -3,
    },

    logoDark: {
      color: isDark
        ? colors.text
        : '#092D5B',
    },

    logoBlue: {
      color: isDark
        ? colors.primary
        : '#2468D4',
    },

    headlineContainer: {
      alignItems: 'center',
      marginTop: 32,
    },

    headlineContainerCompact: {
      marginTop: 15,
    },

    headlineContainerVeryCompact: {
      marginTop: 9,
    },

    headline: {
      fontFamily: DISPLAY_FONT,
      color: isDark
        ? colors.textStrong
        : '#1C1B27',
      fontSize: 33,
      lineHeight: 40,
      fontWeight: '600',
      letterSpacing: -1,
      textAlign: 'center',
    },

    headlineCompact: {
      fontSize: 27,
      lineHeight: 32,
      letterSpacing: -0.75,
    },

    headlineVeryCompact: {
      fontSize: 24,
      lineHeight: 28,
      letterSpacing: -0.6,
    },

    headlineAccent: {
      color: isDark
        ? '#D59BFF'
        : '#A956E8',
    },

    subtitle: {
      marginTop: 19,
      color: isDark
        ? colors.textSecondary
        // Was #928786: 3.42:1 on the #FCFDFF background, under the 4.5:1 WCAG
        // AA minimum, and reported by axe in BASELINE-WEB.md. Moved to a more
        // neutral grey that still clears AA (4.59:1). Dark mode already passes
        // at 8.75:1, so only the light value moves.
        : '#747474',
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      lineHeight: 22,
      fontWeight: '500',
      textAlign: 'center',
      letterSpacing: 0.1,
    },

    subtitleCompact: {
      marginTop: 9,
      fontSize: 14,
      lineHeight: 19,
    },

    subtitleVeryCompact: {
      marginTop: 6,
      fontSize: 13,
      lineHeight: 17,
    },

    radarFrame: {
      position: 'relative',
      alignSelf: 'center',
    },

    radar: {
      position: 'absolute',
      width: 310,
      height: 310,
      alignItems: 'center',
      justifyContent: 'center',
    },

    radarRing: {
      position: 'absolute',
      borderWidth: 1,
      borderColor: isDark
        ? 'rgba(110, 168, 255, 0.24)'
        : 'rgba(82, 137, 222, 0.16)',
    },

    radarRingOuter: {
      width: 310,
      height: 310,
      borderRadius: 155,
    },

    radarRingMiddle: {
      width: 238,
      height: 238,
      borderRadius: 119,
    },

    radarRingInner: {
      width: 166,
      height: 166,
      borderRadius: 83,
    },

    radarGlow: {
      width: 145,
      height: 145,
      borderRadius: 73,
      backgroundColor: isDark
        ? 'rgba(53, 91, 142, 0.46)'
        : 'rgba(221, 234, 255, 0.65)',
      shadowColor: isDark
        ? colors.primary
        : '#8CB4F2',
      shadowOpacity: 0.3,
      shadowRadius: 32,
      shadowOffset: {
        width: 0,
        height: 0,
      },
    },

    userBubble: {
      position: 'absolute',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDark
        ? colors.surfaceSoft
        : '#F0F5FC',
      borderWidth: 1,
      borderColor: isDark
        ? colors.borderSoft
        : '#EDF2F8',
      shadowColor: isDark
        ? colors.shadow
        : '#8FA7C8',
      shadowOpacity: 0.2,
      shadowRadius: 8,
      shadowOffset: {
        width: 0,
        height: 4,
      },
      elevation: 4,
    },

    userBubbleStrong: {
      backgroundColor: isDark
        ? colors.primary
        : '#2467D7',
      borderColor: isDark
        ? colors.primary
        : '#2467D7',
      shadowColor: isDark
        ? colors.primaryStrong
        : '#1E58B9',
      shadowOpacity: 0.34,
      shadowRadius: 12,
      elevation: 7,
    },

    userTopLeft: {
      top: 45,
      left: 32,
    },

    userTopRight: {
      top: 82,
      right: 14,
    },

    userBottomLeft: {
      bottom: 51,
      left: 43,
    },

    userCenter: {
      top: 124,
      left: 124,
    },

    messageBubble: {
      position: 'absolute',
      top: 103,
      left: 190,
      minWidth: 66,
      height: 48,
      paddingHorizontal: 18,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 24,
      backgroundColor: isDark
        ? colors.surface
        : '#FFFFFF',
      shadowColor: isDark
        ? colors.shadow
        : '#9BAFCB',
      shadowOpacity: 0.16,
      shadowRadius: 11,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    messageTail: {
      position: 'absolute',
      bottom: 4,
      left: -3,
      width: 14,
      height: 14,
      borderBottomLeftRadius: 4,
      backgroundColor: isDark
        ? colors.surface
        : '#FFFFFF',
      transform: [
        {
          rotate: '35deg',
        },
      ],
    },

    messageText: {
      color: isDark
        ? colors.textStrong
        : '#13355E',
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      fontWeight: '600',
    },

    actions: {
      gap: 12,
      marginTop: 20,
    },

    actionsCompact: {
      gap: 9,
      marginTop: 11,
    },

    actionsVeryCompact: {
      gap: 7,
      marginTop: 7,
    },

    authButton: {
      height: 56,
      borderRadius: 14,
      overflow: 'hidden',
      shadowColor: isDark
        ? colors.primaryStrong
        : '#205BBC',
      shadowOpacity:
        Platform.OS === 'web'
          ? isDark
            ? 0.04
            : 0.08
          : isDark
            ? 0.015
            : 0.03,
      shadowRadius:
        Platform.OS === 'web'
          ? isDark
            ? 3
            : 5
          : isDark
            ? 1
            : 2,
      shadowOffset: {
        width: 0,
        height: Platform.OS === 'web'
          ? isDark
            ? 1
            : 2
          : 1,
      },
      elevation: Platform.OS === 'android' ? 0 : 1,
    },

    authButtonSecondaryShell: {
      borderWidth: 1,
      borderColor: isDark
        ? colors.border
        : '#DCE2EA',
    },

    authButtonPrimaryShell: {
      backgroundColor: isDark
        ? colors.primary
        : '#1760D9',
      // Android clips a rounded `overflow: 'hidden'` view so aggressively that
      // it can drop EVERY child while still painting the view's own background.
      // "Send me a code" hit exactly that: the button was a solid blue slab
      // with no label and no icon. Proven, not guessed — the row measured
      // 300x48 at 0,0, and a bare absolutely-positioned test square placed
      // beside it did not paint either, so the fault was the clip and not the
      // row. Turning the clip off brought both back.
      //
      // Safe only on this path. Android primary paints its background HERE, on
      // the Pressable, which rounds itself; nothing needs clipping. The other
      // paths put their fill in `authButtonFill`, which has no radius of its
      // own and would show square corners without the clip — so they keep it.
      //
      // Why it only bit one button: the same styles render fine elsewhere on
      // the screen. Something about this subtree (the panel is composited with
      // the keyboard open) triggers it, which is why the fix is scoped rather
      // than global.
      ...(Platform.OS === 'android' ? { overflow: 'visible' as const } : null),
    },

    authButtonCompact: {
      height: 48,
      borderRadius: 12,
    },

    authButtonVeryCompact: {
      height: 44,
      borderRadius: 11,
    },

    secondaryButton: {
      backgroundColor: isDark
        ? colors.surface
        : '#FFFFFF',
    },

    authButtonFill: {
      flex: 1,
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },

    authButtonContent: {
      width: '100%',
      height: '100%',
      paddingHorizontal: 22,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },

    authButtonIcon: {
      marginRight: 13,
      alignItems: 'center',
      justifyContent: 'center',
    },

    authButtonLabel: {
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 22,
      fontWeight: '600',
      letterSpacing: 0.05,
      color: '#FFFFFF',
      textAlign: 'center',
    },

    authButtonLabelCompact: {
      fontSize: 15.5,
      lineHeight: 21,
    },

    authButtonLabelVeryCompact: {
      fontSize: 15,
      lineHeight: 20,
    },

    primaryButtonLabel: {
      color: '#FFFFFF',
    },

    secondaryButtonLabel: {
      color: isDark
        ? colors.textStrong
        : '#122D50',
    },

    buttonPressed: {
      opacity: 0.96,
      transform: [
        {
          scale: 0.99,
        },
      ],
    },

    buttonDisabled: {
      opacity: 0.55,
    },

    dividerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      marginTop: 13,
      paddingHorizontal: 14,
    },

    dividerRowCompact: {
      gap: 12,
      marginTop: 5,
    },

    dividerRowVeryCompact: {
      marginTop: 2,
    },

    dividerLine: {
      flex: 1,
      height:
        StyleSheet.hairlineWidth,
      backgroundColor: isDark
        ? colors.divider
        : '#CCD4DF',
    },

    dividerOrnament: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },

    dividerRing: {
      width: 9,
      height: 9,
      borderRadius: 4.5,
      borderWidth: 1.5,
      borderColor: isDark ? colors.divider : '#CCD4DF',
    },

    dividerDot: {
      width: 5,
      height: 5,
      borderRadius: 2.5,
    },

    dividerDotPrimary: {
      backgroundColor: colors.primary,
    },

    dividerDotStrong: {
      backgroundColor: colors.primaryStrong,
    },

    legalText: {
      color: isDark
        ? colors.textSecondary
        // Was #7C879B: 3.56:1 on #FCFDFF, under the 4.5:1 AA minimum. Darkened
        // to the nearest same-hue value clearing it (4.63:1).
        : '#697489',
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 20,
      textAlign: 'center',
    },

    legalTextCompact: {
      fontSize: 11.5,
      lineHeight: 16,
    },

    legalTextVeryCompact: {
      fontSize: 10.5,
      lineHeight: 14,
    },

    linkText: {
      color: isDark
        ? colors.primaryStrong
        : '#2364CB',
      // The Terms / Privacy links sit inside a sentence, and colour alone was
      // the only thing marking them: axe reported `link-in-text-block` at
      // 1.54:1 against the surrounding text (3:1 required) in light mode and
      // 1.06:1 in dark. Darkening the surrounding text does NOT fix it — it
      // moves the light figure to 1.19:1. Font weight satisfies WCAG 1.4.1
      // without depending on colour alone, while keeping the sentence visually
      // calmer than an underline.
      fontWeight: '600',
    },

    emailPanel: {
      gap: 18,
      padding: 21,
      marginTop: 18,
      marginBottom: 16,
      borderRadius: 22,
      backgroundColor: isDark
        ? colors.surface
        : '#FFFFFF',
      borderWidth: 1,
      borderColor: isDark
        ? colors.border
        : '#E3E9F1',
      shadowColor: isDark
        ? colors.shadow
        : '#6D86AA',
      shadowOpacity: 0.14,
      shadowRadius: 22,
      shadowOffset: {
        width: 0,
        height: 10,
      },
      elevation: 5,
    },

    emailPanelCompact: {
      gap: 14,
      padding: 17,
      marginTop: 8,
      marginBottom: 6,
      borderRadius: 19,
    },

    emailHeading: {
      gap: 5,
    },

    emailTitle: {
      color: isDark
        ? colors.text
        : '#102D52',
      fontFamily: FONT_FAMILY,
      fontSize: 23,
      fontWeight: '600',
    },

    emailDescription: {
      color: isDark
        ? colors.textSecondary
        : '#7B879A',
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 20,
    },

    inputLabel: {
      marginBottom: 8,
      color: isDark
        ? colors.textBody
        : '#253E61',
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: '600',
    },

    inputContainer: {
      height: 54,
      paddingHorizontal: 16,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      borderWidth: 1,
      borderColor: isDark
        ? colors.borderSoft
        : '#D8E0EA',
      borderRadius: 12,
      backgroundColor: isDark
        ? colors.surfaceMuted
        : '#FAFBFD',
    },

    inputContainerError: {
      borderColor: isDark
        ? colors.dangerStrong
        : '#D94A55',
    },

    input: {
      flex: 1,
      height: '100%',
      // Inset the text/placeholder so it isn't flush against the focus
      // highlight that hugs the input on web.
      paddingLeft: 8,
      color: isDark
        ? colors.text
        : '#102D52',
      fontFamily: FONT_FAMILY,
      fontSize: 16,
    },

    errorText: {
      marginTop: 6,
      color: isDark
        ? colors.dangerStrong
        : '#D23F4B',
      fontFamily: FONT_FAMILY,
      fontSize: 12,
    },

    backButton: {
      height: 40,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
    },

    backButtonText: {
      color: isDark
        ? colors.primaryStrong
        : '#245FC8',
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: '600',
    },
  });
}
